import { useCallback, useEffect, useRef, useState } from "react"

import {
  AudioVideoFacade,
  DefaultDeviceController,
  DefaultMeetingSession,
  MeetingSessionConfiguration,
  NoOpLogger,
} from "amazon-chime-sdk-js"
import camelize from "camelize"

import airbrake from "src/airbrake-notifier"

import useLocalStorage from "hooks/useLocalStorage"

import { buildDidStartHandler, createVoiceFocusTransformer } from "./helpers"
import { ChimeJoinInfo, HandleAudioRoomError } from "./types"
import useRoster from "./useRoster"

const useAudioRoom = (
  joinInfo: ChimeJoinInfo,
  onFatalError: (message: string) => void,
  audioRoomId: string,
  currentUserId: string,
  audioInputDeviceId: string
) => {
  const audioVideoRef = useRef<AudioVideoFacade>()
  const [audioVideo, setAudioVideo] = useState<AudioVideoFacade>()
  const [connected, setConnected] = useState(false)
  const [muted, setMuted] = useLocalStorage(
    `audioRoom:${audioRoomId}:user:${currentUserId}:muted`,
    true
  )
  const { roster } = useRoster({
    audioVideo,
  })
  const handleError: HandleAudioRoomError = useCallback(
    ({ airbrakeMessage, error }) => {
      if (airbrakeMessage)
        airbrake.notify({
          message: airbrakeMessage,
          error,
          params: {
            name: "AudioRoom/useAudioRoom.ts",
            roomId: audioRoomId,
            userId: currentUserId,
          },
        })
    },
    [audioRoomId, currentUserId]
  )

  useEffect(() => {
    let cancelled = false
    let observer: { audioVideoDidStart: () => Promise<void> }

    const chimeErrorHandler = (error: Error) => {
      onFatalError("Something went wrong. Please refresh to try again.")
      handleError({
        airbrakeMessage: "Fatal error caught from Chime",
        error,
      })
    }

    const setup = async () => {
      const transformer = await createVoiceFocusTransformer(handleError)
      if (cancelled) return

      const logger = new NoOpLogger()
      const deviceController = new DefaultDeviceController(logger, {
        enableWebAudio: transformer != null,
      })
      const configuration = new MeetingSessionConfiguration(
        camelize(joinInfo.meeting),
        camelize(joinInfo.attendee)
      )
      const meetingSession = new DefaultMeetingSession(
        configuration,
        logger,
        deviceController
      )
      audioVideoRef.current = meetingSession.audioVideo
      setAudioVideo(audioVideoRef.current)

      audioVideoRef.current.realtimeSubscribeToFatalError(chimeErrorHandler)

      const audioVideoDidStart = buildDidStartHandler(
        audioVideoRef.current,
        setConnected,
        handleError,
        onFatalError,
        transformer,
        audioInputDeviceId
      )

      observer = { audioVideoDidStart }
      audioVideoRef.current.addObserver(observer)
      audioVideoRef.current.start()
    }

    setup()

    return () => {
      cancelled = true
      setConnected(false)

      if (!audioVideoRef.current) return

      if (observer) audioVideoRef.current.removeObserver(observer)
      audioVideoRef.current.stopAudioInput()
      audioVideoRef.current.stop()
      audioVideoRef.current.realtimeUnsubscribeToFatalError(chimeErrorHandler)
      audioVideoRef.current = null
      setAudioVideo(null)
    }
  }, [joinInfo, handleError, onFatalError, audioInputDeviceId])

  useEffect(() => {
    if (!audioVideo) return

    if (muted) {
      audioVideo.realtimeMuteLocalAudio()
    } else {
      audioVideo.realtimeUnmuteLocalAudio()
    }
  }, [audioVideo, muted])

  const mute = useCallback(() => setMuted(true), [setMuted])

  const unmute = useCallback(() => setMuted(false), [setMuted])

  return { connected, muted, mute, unmute, roster, audioVideo }
}

export default useAudioRoom
